/* eslint-disable no-nested-ternary */
import { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import TopicGroupCard from '@comp/TopicGroupCard/TopicGroupCard';
import H4 from '@base/H4/H4';
import Link from '@base/Link/Link';
import Grow from '@base/Grow/Grow';
import P1 from '@base/P1/P1';
import { segment } from '@actions/analytics';
import TopicCard from '@cont/Syllabus/HomePage/TopicCard';
import { Fonts, Spacings, breakpoints, mediaQuery } from '@styles/index';
import Button from '@base/Button/Button';
import pushRoute from '@utils/pushRoute';
import { getIsSyllabusV1Enabled } from '@selectors/topologies';
import { useIsMobile } from '@stores/AppCommonData';
import NextLink from 'next/link';
import { getTopicLink } from '@models/Topology';
import { getIsGTPGoal } from '@api/selectors/constants';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { GOAL_PAGE, GTP_SEE_ALL_CLICKED } from '@constants/segment';
import useConstants from '@api/hooks/useConstants';

const BoldP1 = styled(P1)`
  font-weight: ${Fonts.BOLD};
`;
const StyH4 = styled(H4)`
  text-transform: capitalize;
  color: var(--color-text-primary);
`;

const LabelDiv = styled.div`
  grid-column: span 18;
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_7B};
  align-items: center;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyTopicGroupCard = styled(TopicGroupCard)`
  ${({ $index, $length, $isPlus }) =>
    $index > 2 &&
    ($isPlus || $index === $length - 1) &&
    `margin-bottom: ${Spacings.SPACING_8B}`}
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: 16px;
  }
`;

const SeeAllLink = styled.div`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const SeeAllButton = styled(Button)`
  display: none;
  @media (max-width: 47.95em) {
    display: block;
    min-height: 40px;
    height: 40px;
    width: 100%;
    background: var(--color-i-white);
    border: 1px solid var(--color-divider);
    border-radius: 8px;
    color: var(--color-i-green);
    font-size: 14px;
    margin: 16px 0 56px;
  }
`;

const StyledTopicCard = styled(TopicCard)`
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${({ $marginBottom }) => $marginBottom};
    width: 100%;
  }
`;

const TopicFeedBlock = ({
  topicFeedBlock,
  goal,
  goalUID,
  viewAllLink,
  viewAllHref,
  type,
  renderProgress,
  renderCourses,
  hideIfNoCourses,
  dataAnalytics,
  lastPrimarySource
}) => {
  const viewRef = useRef();
  const isMobile = useIsMobile();
  const topics = topicFeedBlock.topologies || [];
  const { label } = topicFeedBlock;
  const isFree = type === 'freeCourse';
  const isSyllabusV1Enabled = useSelector((state) =>
    getIsSyllabusV1Enabled(state, goalUID)
  );
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const goalSegmentData = useGoalEventProps({ goalUID });
  const isSeeAllButtonShown = viewAllLink && !isFree;

  const { isSyllabusV1Enabled: defaultIsSyllabusV1Enabled } = goal;
  const isNewSyllabusEnabled =
    defaultIsSyllabusV1Enabled || isSyllabusV1Enabled;

  const handleEvent = () => {
    if (isGTP) {
      segment.track(GTP_SEE_ALL_CLICKED, {
        ...goalSegmentData,
        last_primary_source: GOAL_PAGE,
        last_primary_source_section: 'Syllabus see all'
      });
    }
  };

  const renderTopicViews = () => {
    if (isNewSyllabusEnabled) {
      const topicsWithSubItem = topics
        .filter((topic) => topic.totalSubItem)
        .slice(0, 6);
      return topicsWithSubItem.map((topic, idx) => (
        <NextLink
          href={getTopicLink(topic, goal)}
          key={topic.uid}
          legacyBehavior
        >
          <StyledTopicCard
            title={topic.name}
            chaptersCount={topic.totalSubItem}
            color={topic.color}
            countSubtext={isGTP && 'sub-system'}
            $marginBottom={
              !isSeeAllButtonShown && idx === topicsWithSubItem.length - 1
                ? Spacings.SPACING_14B
                : Spacings.SPACING_4B
            }
          />
        </NextLink>
      ));
    }

    const courseType = type;
    const trimmedTopics =
      lastPrimarySource.toLowerCase() === 'browse'
        ? topics.slice(0, 6)
        : topics;
    const noOfTopics = trimmedTopics.length;
    return trimmedTopics.map((topic, _index) => {
      if (isMobile && _index > 2 && viewAllLink) return null;
      return (
        <StyTopicGroupCard
          index={_index}
          key={topic.uid}
          color={topic.color}
          topology={topic}
          topicUID={topic.uid}
          goal={goal}
          courseType={courseType}
          renderProgress={renderProgress}
          renderCourses={renderCourses}
          hideIfNoCourses={hideIfNoCourses}
          $index={_index}
          $length={noOfTopics}
          $viewAllLink={viewAllLink}
          $isPlus={type === 'plus'}
          dataAnalytics={dataAnalytics}
          label={label}
          className={!viewAllLink && _index === noOfTopics - 1 ? 'last' : ''}
        />
      );
    });
  };
  const seeAllAnalytics = () => {
    handleEvent();
  };

  const onClickSeeAll = () => {
    handleEvent();
    pushRoute(viewAllHref, viewAllLink);
  };

  const topicsToRender = renderTopicViews();

  if (!topicsToRender.length) {
    return null;
  }

  return (
    <>
      {type !== 'syllabus' && (
        <LabelDiv ref={viewRef}>
          <StyH4>{label}</StyH4>
          <Grow />
          {isSeeAllButtonShown && (
            <SeeAllLink>
              <Link
                href={viewAllHref}
                as={viewAllLink}
                onClick={seeAllAnalytics}
              >
                <BoldP1 color="var(--color-i-green)">SEE ALL</BoldP1>
              </Link>
            </SeeAllLink>
          )}
        </LabelDiv>
      )}
      {topicsToRender}
      {isSeeAllButtonShown && (
        <SeeAllButton label="See All" onClick={onClickSeeAll} />
      )}
    </>
  );
};

export default TopicFeedBlock;

TopicFeedBlock.propTypes = {
  topicFeedBlock: PropTypes.objectOf(PropTypes.unknown).isRequired,
  goal: PropTypes.objectOf(PropTypes.unknown).isRequired,
  viewAllLink: PropTypes.string.isRequired,
  viewAllHref: PropTypes.string.isRequired,
  type: PropTypes.string,
  renderProgress: PropTypes.func,
  renderCourses: PropTypes.func,
  hideIfNoCourses: PropTypes.bool,
  dataAnalytics: PropTypes.string.isRequired,
  lastPrimarySource: PropTypes.string
};

TopicFeedBlock.defaultProps = {
  type: '',
  renderProgress: undefined,
  renderCourses: undefined,
  hideIfNoCourses: false,
  lastPrimarySource: ''
};
